import React, { useEffect, useMemo, useState } from 'react';
import { Backdrop, Box, Button, Container, Fade, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { createNotification } from '../../../utils/create-notification';
import LanguageSelectorMain from '../../../component/LanguageSelectorMain';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClose } from '@fortawesome/free-solid-svg-icons';
import Loader from '../../../component/InfiniteLoader/InfiniteLoader';
import SimpleLoader from '../../../component/loader';
import DocumentTranslationLoaderWithProgress from './DocumentTranslationLoaderWithProgress';
import stripe from 'assets/stripe.png';
import { CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import Modal from 'component/Modal';
import { useSelector } from 'react-redux';
import api from 'utils/axios';
import Visa from 'assets/credit-cards/visa.svg';
import Amex from 'assets/credit-cards/amex.svg';
import Mastercard from 'assets/credit-cards/mastercard.svg';
import Unknown from 'assets/credit-cards/unknown.svg';
import { useNavigate } from 'react-router-dom';
import GlossaryModal from 'component/Modal/GlossaryModal';

const CREDIT_CARD_LOGOS = {
	Visa: Visa,
	Amex: Amex,
	Mastercard: Mastercard,
};

// eslint-disable-next-line react/prop-types
const CheckoutForm = ({ name, email, totalWords, handleTranslation }) => {
	const [checkoutLoading, setCheckoutLoading] = useState(false);
	const stripe = useStripe();
	const elements = useElements();
	const navigation = useNavigate();
	const { profileInformation } = useSelector((store) => store?.user);

	useEffect(() => {
		if (!profileInformation?.billingInfo?.paymentMethods[0]) {
			navigation(
				'/billing-information?error=Please update your billing Information'
			);
		}
	}, []);

	const logo = useMemo(() => {
		if (!profileInformation?.billingInfo?.paymentMethods[0]?.brand)
			return Unknown;
		return (
			CREDIT_CARD_LOGOS[
				profileInformation?.billingInfo?.paymentMethods[0]?.brand
			] || Unknown
		);
	}, [profileInformation]);

	const handleSubmit = async (event) => {
		setCheckoutLoading(true);
		event.preventDefault();
		setCheckoutLoading(false);
		handleTranslation();
	};

	return (
		<>
			{checkoutLoading && <SimpleLoader />}
			<form onSubmit={handleSubmit}>
				<div className="flex flex-row justify-between">
					<div className="flex flex-col">
						<div className="flex items-center">
							{profileInformation?.billingInfo?.paymentMethods[0]?.expYear && (
								<img src={logo} alt="Start Icon" className="w-6 h-6 mr-2" />
							)}
							<span className="mr-2">
								{profileInformation?.billingInfo?.paymentMethods[0]?.expYear &&
									'****'}
								{profileInformation?.billingInfo?.paymentMethods[0]?.last4}
							</span>
						</div>
					</div>
					<div className="flex flex-col justify-end">
						<span className="text-right">
							{profileInformation?.billingInfo?.paymentMethods[0]?.expMonth}
							{profileInformation?.billingInfo?.paymentMethods[0]?.expYear &&
								'/'}
							{profileInformation?.billingInfo?.paymentMethods[0]?.expYear}
						</span>
					</div>
				</div>

				<div className="flex justify-between items-center pt-1">
					<label htmlFor="name" className="block text-gray-700 font-bold pt-3">
						Name
					</label>
					<input
						type="text"
						id="name"
						className="bg-white pt-3 rounded-lg text-right"
						placeholder="Enter your name"
						disabled // Disable the input field
						value={name}
					/>
				</div>

				<div className="flex justify-between items-center pt-2">
					<label htmlFor="email" className="block text-gray-700 font-bold ">
						Email
					</label>
					<input
						type="email"
						id="email"
						className="bg-white text-right w-full rounded-lg"
						placeholder="Enter your email"
						disabled // Disable the input field
						value={email}
					/>
				</div>

				<div className="flex justify-between items-center pt-2">
					<label htmlFor="name" className="block text-gray-700 font-bold">
						Total Words
					</label>
					<input
						type="text"
						id="name"
						className="bg-white rounded-lg text-right"
						placeholder="1021"
						disabled // Disable the input field
						value={totalWords}
					/>
				</div>
				<div className="flex justify-between items-center pt-2">
					<label htmlFor="name" className="block text-gray-700 font-bold">
						Per Words
					</label>
					<input
						type="text"
						id="name"
						className="bg-white  rounded-lg text-right"
						placeholder="$0.11"
						disabled // Disable the input field
					/>
				</div>

				<div className="flex justify-between items-center pt-2">
					<label htmlFor="name" className="block text-gray-700 font-bold ">
						Total Cost
					</label>
					<input
						type="text"
						id="name"
						className="bg-white rounded-lg text-right"
						placeholder="$200"
						disabled // Disable the input field
						value={'$' + (totalWords * 0.11).toFixed(2)}
					/>
				</div>
				<div className="flex justify-center items-center mt-5">
					<button
						type="submit"
						disabled={!stripe}
						className="w-full mt-5 bg-[#6db7f2] hover:bg-blue-400 text-white font-bold py-4 rounded-lg   sm:ml-0"
					>
						Pay
					</button>
				</div>
			</form>
		</>
	);
};

const DocumentTranslation = () => {
	const [loading, setLoading] = useState(false);
	const [simpleLoading, setSimpleLoading] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [selectedFile, setSelectedFile] = useState(false);
	const [selectedFileIgnorWords, setSelectedFileIgnoreWords] = useState(false);
	const [value, setValue] = React.useState('1');
	const [rightvalue, setRightValue] = React.useState('2');
	const [wordsCount, setWordsCount] = React.useState('...');
	const [open, setOpen] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [isDragOver, setIsDragOver] = useState(false);
	const [currentRequestId, setCurrentRequestId] = useState(null);

	const [selectedLang, seSelectedLang] = React.useState({
		sourceLang: 'English',
		targetLang: 'English',
	});
	const [languageCode, setLanguageCode] = React.useState({
		sourceLangCode: 'auto',
		targetLangCode: 'en',
	});
	const { control, watch, getValues, setValue: RfSetValue } = useForm();
	const { profileInformation } = useSelector((store) => store?.user);

	const navigation = useNavigate();

	const fetchWords = async () => {
		try {
			setSimpleLoading(true);
			const formData = new FormData();
			formData.append('document', selectedFile || selectedFileIgnorWords);
			const { data } = await api.post(
				'translation/document/count-words',
				formData
			);
			setWordsCount(data?.words);
			console.log(data, 'hamza');
			setSimpleLoading(false);
		} catch (error) {
			if (
				error?.response?.status === 400 &&
				error?.response?.data?.error?.includes('File size is too large!')
			) {
				setSelectedFile(null);
			}
			if (error?.response?.data?.error) {
				createNotification(
					'error',
					error?.response?.data?.error || 'something went wrong'
				);
			}
			setSimpleLoading(false);
		}
	};

	const handleChange = (event, newValue) => {
		setValue(newValue);
	};
	const righthandleChange = (event, newValue) => {
		setRightValue(newValue);
	};

	const handleFileInputChange = async (event) => {
		setSelectedFile(event.target.files[0]);
		event.target.value = '';
	};

	// Drag and drop handlers
	const handleDragOver = (event) => {
		event.preventDefault();
		setIsDragOver(true);
	};

	const handleDragLeave = (event) => {
		event.preventDefault();
		setIsDragOver(false);
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDragOver(false);

		const files = event.dataTransfer.files;
		if (files.length > 0) {
			const file = files[0];
			// Check if file type is supported
			const supportedTypes = [
				'.docx',
				'.pdf',
				'.ppt',
				'.pptx',
				'.xlsx',
				'.json',
			];
			const fileExtension = '.' + file.name.split('.').pop().toLowerCase();

			if (supportedTypes.includes(fileExtension)) {
				setSelectedFile(file);
			} else {
				createNotification(
					'error',
					'Please select a supported file type (.docx, .pdf, .ppt, .pptx, .xlsx, .json)'
				);
			}
		}
	};
	const handleFileInputChangeIgnoreWords = async (event) => {
		setSelectedFileIgnoreWords(event.target.files[0]);
		console.log(selectedFileIgnorWords);
		event.target.value = '';
	};

	const handleTranslation = async (paymentId) => {
		setOpen(false);
		const sourceLanguage = languageCode?.sourceLangCode;
		const targetLanguage = languageCode?.targetLangCode;
		if (sourceLanguage && targetLanguage) {
			setLoading(true);
			setShowLoader(true);

			const requestId = `${Date.now()}-${Math.random()
				.toString(36)
				.slice(2, 11)}`;
			setCurrentRequestId(requestId);

			try {
				const formData = new FormData();
				formData.append('document', selectedFile);
				formData.append('sourceLanguage', sourceLanguage);
				formData.append('targetLanguage', targetLanguage);
				formData.append('paymentMethodId', paymentId);
				formData.append('ignoreWordsFile', selectedFileIgnorWords);
				formData.append('requestId', requestId);

				const response = await api.post('translation/document', formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
					responseType: 'blob',
				});
				console.log(response.data);
				// Create a Blob from the response data
				const blob = new Blob([response.data], {
					type: response.headers['content-type'],
				});

				// Create a temporary URL to download the file
				const url = window.URL.createObjectURL(blob);

				// Create an anchor element and programmatically click it to trigger the download
				const link = document.createElement('a');
				link.href = url;
				link.download = 'translated-doc'; // Set the desired file name and extension
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);

				// Release the temporary URL
				window.URL.revokeObjectURL(url);
				setSelectedFile(null);
				setLoading(false);
				setShowLoader(false);
				setCurrentRequestId(null);
			} catch (error) {
				setSelectedFile(null);
				setLoading(false);
				setShowLoader(false);
				setCurrentRequestId(null);
				if (error?.response?.status === 504) {
					navigation('/document-translation-history');
					return;
				}

				if (error?.code === 'ECONNABORTED' || error?.code === 'ECONNRESET') {
					navigation('/document-translation-history');
					return;
				}

				let errorMessage = 'An error occurred during translation. Please try again.';

				try {
					// Handle Blob response
					if (error?.response?.data && error.response.data instanceof Blob) {
						const text = await error.response.data.text();
						const jsonData = JSON.parse(text);
						errorMessage =
							jsonData?.error ||
							jsonData?.message ||
							jsonData?.msg ||
							errorMessage;
					}
					// Handle JSON response directly
					else if (error?.response?.data && typeof error.response.data === 'object') {
						errorMessage =
							error.response.data?.error ||
							error.response.data?.message ||
							error.response.data?.msg ||
							errorMessage;
					}
					// Handle string response
					else if (error?.response?.data && typeof error.response.data === 'string') {
						errorMessage = error.response.data;
					}
					// Handle axios error message
					else if (error?.message) {
						errorMessage = error.message;
					}
				} catch (parseError) {
					console.error('Error parsing error response:', parseError);
					// Fallback to a more helpful message
					if (error?.response?.status) {
						errorMessage = `Server error (${error.response.status}). Please try again later.`;
					} else if (error?.message) {
						errorMessage = error.message;
					}
				}

				if (
					error?.response?.status === 409 &&
					errorMessage?.includes(
						'Document is already translated or in progress'
					)
				) {
					createNotification('error', 'Error', errorMessage);
					navigation('/document-translation-history');
					return;
				}

				console.error('Error uploading file:', error);
				createNotification('error', 'Error', errorMessage);
			}
		} else {
			setSelectedFile(null);
			setLoading(false);
			setShowLoader(false);
			setCurrentRequestId(null);
			createNotification(
				'error',
				'Error',
				'Please Select Source and Target Languages.'
			);
			setSelectedFile(null);
		}
	};

	const goToPayment = async () => {
		function checkEmail(email) {
			const emailList = [
				'tiffany@managedhealth.com',
				'muriel@managedhealth.com',
				'ernie@managedhealth.com',
				'fatima@managedhealth.com',
				'moe@managedhealth.com',
			];
			return emailList.includes(email);
		}

		if (checkEmail(profileInformation?.email)) {
			handleTranslation('charge_with_one_saved_card');
		} else if (
			profileInformation?.servicesPayment?.find((el) => el === 'document')
		) {
			handleTranslation('not_charge_with_access');
		} else {
			setOpen(true);
		}
	};

	const handleLangCode = (val) => {
		val?.type === 'sourceLanguage' &&
			setLanguageCode({ ...languageCode, sourceLangCode: val?.code });
		val?.type === 'targetLanguage' &&
			setLanguageCode({ ...languageCode, targetLangCode: val?.code });
	};

	const handleLanguageSwitch = () => {
		let targetLang = getValues('targetLanguage');
		let sourceLang = getValues('sourceLanguage');

		targetLang &&
			sourceLang &&
			RfSetValue(
				'targetLanguage',
				sourceLang ?? { label: 'English', value: 'en' }
			);
		setLanguageCode({
			sourceLangCode: languageCode?.targetLangCode,
			targetLangCode: languageCode?.sourceLangCode,
		});
		targetLang &&
			sourceLang &&
			RfSetValue(
				'sourceLanguage',
				targetLang ?? { label: 'English', value: 'en' }
			);
		if (!targetLang && !sourceLang) {
			setLanguageCode({
				sourceLangCode: languageCode?.targetLangCode,
				targetLangCode: languageCode?.sourceLangCode,
			});
			languageCode?.targetLangCode === 'es' && setValue('3');
			languageCode?.targetLangCode === 'en' && setValue('2');
			languageCode?.targetLangCode === 'fr' && setValue('4');

			languageCode?.sourceLangCode === 'es' && setRightValue('3');
			languageCode?.sourceLangCode === 'en' && setRightValue('2');
			languageCode?.sourceLangCode === 'fr' && setRightValue('4');
		}
	};

	useEffect(() => {
		let targetLang = getValues('targetLanguage');
		let sourceLang = getValues('sourceLanguage');
		if (targetLang?.value || sourceLang?.value) {
			seSelectedLang({
				sourceLang: sourceLang?.label,
				targetLang: targetLang?.label,
			});
			setLanguageCode({
				sourceLangCode: sourceLang ? sourceLang?.value : 'auto',
				targetLangCode: targetLang ? targetLang?.value : 'en',
			});
			if (targetLang?.label && targetLang?.label != selectedLang?.targetLang)
				setRightValue('2');
			if (sourceLang?.label && sourceLang?.label != selectedLang?.sourceLang)
				setValue('2');
		}
	}, [watch()?.sourceLanguage, watch()?.targetLanguage]);

	useEffect(() => {
		if (selectedFile) {
			fetchWords();
		}
	}, [selectedFile]);

	return (
		<>
			{showLoader && (
				<DocumentTranslationLoaderWithProgress
					loading={loading}
					sourceLanguage={languageCode?.sourceLangCode}
					targetLanguage={languageCode?.targetLangCode}
					wordCount={wordsCount}
					onClose={() => setShowLoader(false)}
					requestId={currentRequestId}
					userId={profileInformation?._id}
				/>
			)}
			{simpleLoading && <SimpleLoader />}
			<Box>
				<Container className="mt-5">
					<LanguageSelectorMain
						handleChange={handleChange}
						control={control}
						righthandleChange={righthandleChange}
						value={value}
						setValue={setValue}
						rightvalue={rightvalue}
						setRightValue={setRightValue}
						selectedLang={selectedLang}
						setLanguageCode={handleLangCode}
						getValues={getValues}
						handleLanguageSwitch={handleLanguageSwitch}
					/>
					<Box className="relative bg-white rounded-lg shadow-lg pb-20">
						<div className="flex flex-col justify-center items-center">
							<div className="text-center pt-10 mb-8">
								<p className="text-lg text-[#00000099]">
									Documents are required to be under 200MB in size and should
									not exceed 80 pages.
								</p>
							</div>
							<div className="flex flex-col justify-center items-center space-y-8 w-full max-w-md mx-auto">
								{/* Header Section */}
								<div className="text-center space-y-4">
									<div className="w-16 h-16 bg-gradient-to-br from-[#6db7f2] to-[#5daff7] rounded-2xl flex items-center justify-center mx-auto shadow-lg">
										<svg
											className="w-8 h-8 text-white"
											fill="none"
											stroke="currentColor"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												strokeWidth={2}
												d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
											/>
										</svg>
									</div>
									<h2 className="text-2xl font-bold text-gray-800">
										Upload Your Document
									</h2>
									<p className="text-gray-600 text-sm leading-relaxed">
										Choose a document to get started with translation
									</p>
								</div>

								{/* File Upload Section */}
								<div className="w-full space-y-6">
									{/* Upload Area */}
									<div className="relative">
										<input
											type="file"
											className="absolute top-0 left-0 right-0 bottom-0 opacity-0 cursor-pointer z-10"
											onChange={handleFileInputChange}
											accept=".docx,.pdf,.ppt,.pptx,.xlsx,.json"
										/>
										<div
											className={`border-2 border-dashed rounded-2xl p-8 text-center transition-all duration-300 group ${
												isDragOver
													? 'border-[#6db7f2] bg-blue-50/50 scale-105'
													: 'border-gray-300 hover:border-[#6db7f2] hover:bg-blue-50/30'
											}`}
											onDragOver={handleDragOver}
											onDragLeave={handleDragLeave}
											onDrop={handleDrop}
										>
											<div className="space-y-4">
												<div
													className={`w-12 h-12 rounded-xl flex items-center justify-center mx-auto transition-colors duration-300 ${
														isDragOver
															? 'bg-[#6db7f2]/20'
															: 'bg-gray-100 group-hover:bg-[#6db7f2]/10'
													}`}
												>
													<svg
														className={`w-6 h-6 transition-colors duration-300 ${
															isDragOver
																? 'text-[#6db7f2]'
																: 'text-gray-400 group-hover:text-[#6db7f2]'
														}`}
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
														/>
													</svg>
												</div>
												<div>
													<p
														className={`text-lg font-semibold transition-colors duration-300 ${
															isDragOver
																? 'text-[#6db7f2]'
																: 'text-gray-700 group-hover:text-[#6db7f2]'
														}`}
													>
														{selectedFile
															? selectedFile.name
															: isDragOver
															? 'Drop your file here'
															: 'Choose a document'}
													</p>
													<p className="text-sm text-gray-500 mt-1">
														{selectedFile
															? 'Click to change file'
															: isDragOver
															? 'Release to upload'
															: 'Drag & drop or click to upload .docx, .pdf, .ppt, .pptx, .xlsx, .json files'}
													</p>
												</div>
											</div>
										</div>
									</div>

									{/* File Info Card */}
									{selectedFile && (
										<div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 relative overflow-hidden">
											{/* Background Pattern */}
											<div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-[#6db7f2]/5 to-transparent rounded-bl-3xl"></div>

											{/* Close Button */}
											<button
												onClick={() => setSelectedFile(null)}
												className="absolute top-4 right-4 w-8 h-8 bg-gray-100 hover:bg-red-50 text-gray-400 hover:text-red-500 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
												title="Remove file"
											>
												<FontAwesomeIcon icon={faClose} className="text-sm" />
											</button>

											{/* Status Indicator */}
											<div className="flex items-center space-x-3 mb-4">
												<div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
												<span className="text-sm font-medium text-gray-600">
													{simpleLoading
														? 'Analyzing document...'
														: 'Document ready'}
												</span>
											</div>

											{/* File Details */}
											<div className="space-y-3">
												<div className="flex justify-between items-center py-2 border-b border-gray-100">
													<span className="text-sm text-gray-500">
														File Name
													</span>
													<span className="text-sm font-medium text-gray-800 truncate max-w-48">
														{selectedFile.name}
													</span>
												</div>
												<div className="flex justify-between items-center py-2 border-b border-gray-100">
													<span className="text-sm text-gray-500">
														File Size
													</span>
													<span className="text-sm font-medium text-gray-800">
														{(selectedFile.size / 1024 / 1024).toFixed(2)} MB
													</span>
												</div>
												{wordsCount && wordsCount !== '...' && (
													<>
														<div className="flex justify-between items-center py-2 border-b border-gray-100">
															<span className="text-sm text-gray-500">
																Word Count
															</span>
															<span className="text-sm font-medium text-gray-800">
																{wordsCount.toLocaleString()}
															</span>
														</div>
														<div className="flex justify-between items-center py-2">
															<span className="text-sm text-gray-500">
																Translation Cost
															</span>
															<span className="text-sm font-bold text-[#6db7f2]">
																${(0.11 * (Number(wordsCount) || 0)).toFixed(2)}
															</span>
														</div>
													</>
												)}
												{simpleLoading && (
													<div className="flex items-center justify-center space-x-2 py-3">
														<div className="animate-spin rounded-full h-4 w-4 border-2 border-[#6db7f2] border-t-transparent"></div>
														<span className="text-sm text-gray-500">
															Counting words...
														</span>
													</div>
												)}
											</div>
										</div>
									)}
								</div>

								{/* Action Buttons */}
								<div className="w-full space-y-3">
									{/* Translate Button - Primary Action */}
									{(selectedFileIgnorWords || selectedFile) && (
										<button
											className="w-full bg-gradient-to-r from-[#6db7f2] to-[#5daff7] hover:from-[#5daff7] hover:to-[#4a9dd9] text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98] group relative overflow-hidden"
											onClick={goToPayment}
										>
											<div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-10 transition-opacity duration-300"></div>
											<span className="relative z-10 flex items-center justify-center space-x-2">
												<svg
													className="w-5 h-5"
													fill="none"
													stroke="currentColor"
													viewBox="0 0 24 24"
												>
													<path
														strokeLinecap="round"
														strokeLinejoin="round"
														strokeWidth={2}
														d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
													/>
												</svg>
												<span>Translate Document</span>
											</span>
										</button>
									)}

									{/* Add Glossary Button - Secondary Action */}
									<button
										onClick={() => setModalOpen(true)}
										className="w-full bg-white border-2 border-[#6db7f2] text-[#6db7f2] hover:bg-[#6db7f2] hover:text-white font-bold py-3 px-8 rounded-xl transition-all duration-300 shadow-sm hover:shadow-md group"
									>
										<span className="flex items-center justify-center space-x-2">
											<svg
												className="w-4 h-4"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={2}
													d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
												/>
											</svg>
											<span>Add Glossary</span>
										</span>
									</button>
								</div>
							</div>
						</div>
					</Box>
					<Box>
						<div className="text-center pt-10">
							<p className="text-lg text-[#00000099]">
								Upon completion of translations, it maybe necessary to format
								the translated documents accordingly.
								<br /> Secure payment transctions are facilitated through
							</p>
							<div className="flex justify-center items-center">
								<img src={stripe} alt="stripe" width={150} />
							</div>
						</div>
					</Box>
				</Container>
			</Box>
			{wordsCount && (
				<Modal
					title={'Payment'}
					open={open}
					handleClose={() => setOpen(false)}
					bodyTopMargin={1}
				>
					<CheckoutForm
						name={profileInformation?.firstName}
						email={profileInformation?.email}
						totalWords={wordsCount}
						handleTranslation={handleTranslation}
					/>
				</Modal>
			)}
			{modalOpen && (
				<GlossaryModal onClose={() => setModalOpen(false)} isOpen={modalOpen} />
			)}
		</>
	);
};

export default DocumentTranslation;
